import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";

const AuthContext = createContext(null);
const HTTP_UNAUTHORIZED = 401;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (error) {
      if (error?.response?.status && error.response.status !== HTTP_UNAUTHORIZED) {
        logError("Auth check error:", error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the /me check while returning from an OAuth callback; the callback pages handle it.
    // Emergent flow uses #session_id in the hash; custom Google flow uses /auth/google?code=...
    const onGoogleCallback = window.location.pathname === "/auth/google" && window.location.search.includes("code=");
    if (window.location.hash?.includes("session_id=") || onGoogleCallback) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const demoLogin = useCallback(async () => {
    const res = await api.post("/auth/demo-login");
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      logError("Logout error:", error);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, demoLogin, logout }),
    [user, loading, checkAuth, demoLogin, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
